import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, RotateCcw, Search, SlidersHorizontal } from 'lucide-react';
import { adminManagerRows, prototypePageConfigs, prototypeRows, roleRows, statusClass, type PrototypePageConfig } from './prototypeData';

function useCurrentConfig(): PrototypePageConfig {
  const pathname = window.location.pathname;
  return prototypePageConfigs.find((config) => config.path === pathname) ?? prototypePageConfigs[0];
}

function getRows(config: PrototypePageConfig) {
  if (config.path === '/admin-management') return adminManagerRows;
  if (config.path === '/role-management') return roleRows;
  return prototypeRows.map((row, rowIndex) => {
    const prefix = config.titleKo.slice(0, 2);
    return row.map((cell, cellIndex) => {
      if (cellIndex === 1) return `${prefix} ${cell}`;
      if (cellIndex === 2 && config.type !== 'table') return ['초안','검토','배포 완료','비공개','공개'][rowIndex % 5];
      return cell;
    });
  });
}

export function AdminPrototypePage() {
  const config = useCurrentConfig();
  const { t, i18n } = useTranslation(['admin', 'common']);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const rows = useMemo(() => getRows(config).filter((row) => {
    const text = row.join(' ').toLowerCase();
    return (!query || text.includes(query.toLowerCase())) && (!status || text.includes(status));
  }), [config, query, status]);
  const title = t(config.titleKey, { ns: 'common', defaultValue: i18n.language.startsWith('vi') ? config.titleVi : i18n.language.startsWith('en') ? config.titleEn : config.titleKo });

  return (
    <>
      <div className="content__header">
        <div>
          <h1 className="content__title">{title}</h1>
          <p className="content__subtitle">{t(config.descriptionKey, { ns: 'admin' })}</p>
        </div>
        <div className="content__actions">
          <button className="btn btn-outline" type="button"><RotateCcw size={16} />{t('actions.reset', { ns: 'common' })}</button>
          <button className="btn btn-primary" type="button"><Plus size={16} />{t('prototype.create', { ns: 'admin' })}</button>
        </div>
      </div>

      <div className="content__body">
        <div className="filter-bar">
          <select className="filter-bar__select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{t('prototype.allStatus', { ns: 'admin' })}</option>
            <option value="활성">{t('prototype.active', { ns: 'admin' })}</option>
            <option value="대기">{t('prototype.pending', { ns: 'admin' })}</option>
            <option value="비활성">{t('prototype.inactive', { ns: 'admin' })}</option>
          </select>
          <div className="filter-bar__search" style={{ flex: 1 }}>
            <input className="filter-bar__search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('prototype.searchPlaceholder', { ns: 'admin' })} />
            <Search className="filter-bar__search-icon" />
          </div>
          <button className="btn btn-outline" type="button"><SlidersHorizontal size={16} />Filter</button>
        </div>

        {config.type === 'detail' ? <DetailPanel title={title} /> : null}
        {config.type === 'settings' ? <SettingsPanel /> : null}
        {config.type === 'editor' ? <EditorPanel /> : null}

        <div className="card">
          <table className="table">
            <thead>
              <tr>{config.columns?.map((column) => <th key={column}>{column}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.join('-')}>
                  {row.slice(0, config.columns?.length ?? row.length).map((cell, index) => (
                    <td key={`${cell}-${index}`}>
                      {index >= row.length - 2 || ['활성','비활성','미가입','주의','긴급','G0','G1','G2','G3','G4','대기'].includes(cell)
                        ? <span className={statusClass(cell)}>{cell}</span>
                        : cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button className="pagination__btn" type="button">‹</button>
          <button className="pagination__btn pagination__btn--active" type="button">1</button>
          <button className="pagination__btn" type="button">2</button>
          <button className="pagination__btn" type="button">3</button>
          <button className="pagination__btn" type="button">›</button>
        </div>
      </div>
    </>
  );
}

function DetailPanel({ title }: { title: string }) {
  return <div className="card" style={{ marginBottom: 16, maxWidth: 860 }}><div style={{ padding: 24, display: 'grid', gap: 12 }}><strong>{title} 기본 정보</strong><div className="detail-grid"><span>기관명</span><b>울산대학교병원</b><span>권한</span><b>Smart Home Care 관리자</b><span>상태</span><b>활성</b></div></div></div>;
}

function SettingsPanel() {
  return <div className="card" style={{ marginBottom: 16 }}><div style={{ padding: 24 }}><div className="form-grid"><label>이메일 알림 <input type="checkbox" defaultChecked /></label><label>SMS 알림 <input type="checkbox" defaultChecked /></label><label>앱 푸시 <input type="checkbox" defaultChecked /></label><label>야간 알림 제한 <input type="checkbox" /></label></div></div></div>;
}

function EditorPanel() {
  return <div className="card" style={{ marginBottom: 16 }}><div style={{ padding: 24 }}><div className="form-grid"><input className="input" placeholder="제목" /><select className="filter-bar__select"><option>공개</option><option>비공개</option><option>검토</option></select></div><textarea className="textarea" rows={4} style={{ marginTop: 16, width: '100%' }} placeholder="프로토타입과 동일한 편집 영역" /></div></div>;
}
